import { useQuery } from '@tanstack/react-query'
import { BookOpen, Boxes, ChevronRight, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge, LangBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState, Separator, Skeleton, Stat } from '@/components/ui/misc'
import { MentionsList } from './mentions-list'
import { getMentions } from '@/lib/api/terms/get-mentions'
import { getTermConcept } from '@/lib/api/terms/get-term-extras'
import { formatNumber } from '@/lib/utils'
import type { Layer, Term } from '@/types/terms'

interface TermDetailProps {
  term: Term | null
  layer: Layer
  onOpenConcept: (conceptId: number) => void
  /** Open a mention's document at its page, with this term highlighted. */
  onOpenDocument?: (documentId: number, page: number) => void
  /** Clears the selection; the pane returns to its overview state. */
  onClose?: () => void
}

export function TermDetail({
  term,
  layer,
  onOpenConcept,
  onOpenDocument,
  onClose,
}: TermDetailProps) {
  const { t } = useTranslation()
  const enabled = !!term

  const mentions = useQuery({
    queryKey: ['mentions', term?.term_id, layer],
    queryFn: () => getMentions(term!.term_id, layer),
    enabled,
  })
  const concept = useQuery({
    queryKey: ['term-concept', term?.term_id, layer],
    queryFn: () => getTermConcept(term!.term_id, layer),
    enabled,
  })

  if (!term) {
    return (
      <EmptyState
        icon={BookOpen}
        title={t('terms.selectTerm')}
        hint={t('terms.selectTermHint')}
      />
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border bg-accent/40 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold leading-tight">{term.value}</h2>
          <LangBadge lang={term.lang} />
          {term.term_type === 'abbreviation' && (
            <Badge variant="outline">{t('terms.abbreviation')}</Badge>
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="ml-auto"
              onClick={onClose}
              title={t('common.close')}
              aria-label={t('common.close')}
            >
              <X />
            </Button>
          )}
        </div>
        {term.domain && (
          <p className="mt-1 text-xs text-muted-foreground">{term.domain}</p>
        )}

        <div className="mt-3 flex gap-6">
          <Stat label={t('terms.statFrequency')} value={formatNumber(term.freq)} />
          <Stat label={t('terms.statDocuments')} value={formatNumber(term.doc_count)} />
          <Stat
            label={t('terms.statMentions')}
            value={
              mentions.isLoading ? (
                <Skeleton className="h-4 w-10" />
              ) : (
                formatNumber(mentions.data?.length ?? 0)
              )
            }
          />
        </div>

        {concept.data && (
          <button
            type="button"
            onClick={() => onOpenConcept(concept.data!.concept_id)}
            className="mt-3 flex w-full items-center gap-2 rounded-md border border-border bg-muted/50 px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted"
          >
            <Boxes className="size-3.5 shrink-0 text-primary" />
            <span className="text-muted-foreground">{t('terms.belongsToConcept')}</span>
            <span className="font-semibold">#{concept.data.concept_id}</span>
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {/* is_preferred is a human judgement the builder does not make,
                  so preferring it but falling back keeps this line populated
                  for machine-built concepts too. */}
              {(concept.data.terms.some((ct) => ct.is_preferred)
                ? concept.data.terms.filter((ct) => ct.is_preferred)
                : concept.data.terms
              )
                .slice(0, 3)
                .map((ct) => `${ct.value} (${ct.lang})`)
                .join(' · ')}
            </span>
            <ChevronRight className="size-3.5 shrink-0" />
          </button>
        )}

      </div>

      <Separator />

      <div className="min-h-0 flex-1">
        <MentionsList
          mentions={mentions.data ?? []}
          isLoading={mentions.isLoading}
          termValue={term.value}
          onOpenDocument={onOpenDocument}
        />
      </div>
    </div>
  )
}
